import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import type { TimeRange } from 'tacho-activity-chart';
import { DriversService } from 'app/services/drivers/drivers.service';
import { VehiclesService } from 'app/services/vehicles/vehicles.service';
import { normalizeDriverDiagram, normalizeVehicleDiagram, type NormalizedDiagram } from 'app/components/tacho-activity-chart/diagram-normalizer';
import type { DriverDiagramDayLike } from 'app/components/tacho-activity-chart/driver-chart-data';
import type { VehicleDiagramDayLike } from 'app/components/tacho-activity-chart/vehicle-chart-data';
import { padDiagramPeriod } from 'app/components/tacho-activity-chart/diagram-period';

const DAY_SECONDS = 24 * 60 * 60;
/** A report page for one entity needs one entry; keep a few for tab flips. */
const MAX_CACHED_PERIODS = 16;

/**
 * Loads `getDiagramDataForPeriod` once per entity and report period.
 *
 * The daily chart report used to request one padded day per rendered day. The
 * endpoint returns whole UTC day buckets for a period, so every day of the
 * report shares the same answer: the first consumer starts one request for the
 * whole period and every other day (and page) reuses it. The normalised result
 * is timezone-free; the caller lays it out for its own days.
 */
@Injectable({
    providedIn: 'root'
})
export class DiagramPeriodLoaderService {

    private readonly cache = new Map<string, Observable<NormalizedDiagram>>();

    constructor(
        private _driversService: DriversService,
        private _vehiclesService: VehiclesService,
    ) {
    }

    /** Activities of one driver for the whole report period. */
    loadDriver(driverId: number, clientId: string | null | undefined, period: TimeRange): Observable<NormalizedDiagram> {
        if (!driverId) { return of(EMPTY_DIAGRAM); }

        const request = diagramRequestPeriod(period);
        const client = clientId == null ? '' : '' + clientId;
        return this.load(`driver|${driverId}|${client}|${request.start}|${request.end}`, () =>
            this._driversService
                .myGetActivitiesForAPeriod(driverId, request.start, request.end, client)
                .pipe(map(response => normalizeDriverDiagram(
                    (response?.data ?? []) as unknown as DriverDiagramDayLike[]))));
    }

    /** Activities of one vehicle for the whole report period. */
    loadVehicle(vehicleId: string, clientId: string | null | undefined, period: TimeRange): Observable<NormalizedDiagram> {
        if (!vehicleId) { return of(EMPTY_DIAGRAM); }

        const request = diagramRequestPeriod(period);
        const client = clientId == null ? '' : '' + clientId;
        return this.load(`vehicle|${vehicleId}|${client}|${request.start}|${request.end}`, () =>
            this._vehiclesService
                .getDiagrammDataForPeriod(vehicleId, client, request.start, request.end)
                .pipe(map(response => normalizeVehicleDiagram(
                    (response?.data ?? []) as unknown as VehicleDiagramDayLike[]))));
    }

    /** Forgets every loaded period; a new report must not reuse old diagrams. */
    clear(): void {
        this.cache.clear();
    }

    private load(key: string, factory: () => Observable<NormalizedDiagram>): Observable<NormalizedDiagram> {
        const cached = this.cache.get(key);
        if (cached) { return cached; }

        const shared = factory().pipe(
            catchError((error) => {
                // A failed request must not be replayed for later days.
                this.cache.delete(key);
                return throwError(error);
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
        this.cache.set(key, shared);
        this.trimCache();
        return shared;
    }

    private trimCache(): void {
        while (this.cache.size > MAX_CACHED_PERIODS) {
            const oldest = this.cache.keys().next().value;
            if (oldest === undefined) { return; }
            this.cache.delete(oldest);
        }
    }
}

/**
 * The diagram endpoint walks whole UTC day buckets, so the request is aligned
 * to UTC midnights and padded by a day on each side. The report day is local:
 * without the padding the first/last local hours would be missing.
 */
export function diagramRequestPeriod(period: TimeRange): TimeRange {
    const start = Number.isFinite(period?.start) ? period.start : 0;
    const end = Number.isFinite(period?.end) ? period.end : start;
    return padDiagramPeriod({
        start: Math.floor(start / DAY_SECONDS) * DAY_SECONDS,
        end: (Math.floor(end / DAY_SECONDS) + 1) * DAY_SECONDS - 1,
    });
}

const EMPTY_DIAGRAM: NormalizedDiagram = {
    days: [],
    activities: [],
    countries: [],
    countryEvents: [],
    driverPeriods: [],
    coDriverPeriods: [],
    vehicles: [],
    outOfScope: [],
    ferry: [],
};
